'use client';

/**
 * Mobile-app promotion band — a single dense, near-black strip that reads
 * left to right: copy column (kicker, two-line headline, one-liner, 2×2
 * icon feature grid, two store badges), then a bare scan-to-download QR,
 * then a pair of upright phones that bleed past the band's bottom edge and
 * are cropped by it.
 *
 * <p>The store badges are the runtime's shared {@link StoreBadge} — the same
 * official artwork the site footer renders — so a tenant's download buttons
 * look identical wherever they appear.
 *
 * <p>Everything else is author-driven: copy, feature icons (a fixed lucide
 * set the editor picks by name), store links, the QR image, and both phone
 * screenshots. Theme tokens supply the accent and fonts.
 *
 * <p>The cropped-phone look is the whole trick: the device column is
 * shorter than the images inside it, and {@code overflow: hidden} on the
 * shell does the cutting — no masks, no fixed pixel heights to keep in
 * sync with the copy column.
 *
 * <p>The devices are the authored screenshots themselves — no drawn bezel,
 * notch or placeholder UI. Whatever the tenant uploads (usually an already
 * mocked-up device render) is what shows; the block only places, leans and
 * crops it.
 */
import { type CSSProperties, type ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import { StoreBadge } from '@akademi/site-builder-runtime';
import {
  Award,
  BadgeCheck,
  Bell,
  BookOpen,
  ChartColumn,
  CircleCheckBig,
  Clock,
  Download,
  MonitorPlay,
  Play,
  ShieldCheck,
  Smartphone,
  Sparkles,
  Target,
  Users,
  WifiOff,
  Zap,
} from 'lucide-react';
import { tx, asList, pickEnum } from '../_shared/coerce';
import { sectionInner } from '../_shared/styles';
import { resolvePaddingStyle, type PaddingBox } from '../_shared/resolvePadding';
import { BlockRoot } from '../_shared/BlockRoot';

type Localized = string | Record<string, string>;

export type MobileAppPromoAccent = 'brand' | 'accent' | 'success' | 'amber';
export type MobileAppPromoImagePosition = 'left' | 'right';
export type MobileAppPromoVariant = 'immersive' | 'compact';

const ACCENT_TOKENS: Record<MobileAppPromoAccent, string> = {
  brand: 'var(--color-brand-500, #3b82f6)',
  accent: 'var(--color-accent-500, #8b5cf6)',
  success: 'var(--color-feedback-success, #10b981)',
  amber: 'var(--color-amber-500, #f59e0b)',
};

/**
 * Feature icons the editor can choose from. Kept to a fixed roster so the
 * admin can offer a dropdown and the bundle only carries what's listed.
 */
const FEATURE_ICONS = {
  download: Download,
  'offline-watch': MonitorPlay,
  'wifi-off': WifiOff,
  zap: Zap,
  clock: Clock,
  target: Target,
  chart: ChartColumn,
  award: Award,
  certificate: BadgeCheck,
  check: CircleCheckBig,
  book: BookOpen,
  bell: Bell,
  shield: ShieldCheck,
  users: Users,
  sparkles: Sparkles,
  play: Play,
  phone: Smartphone,
} satisfies Record<string, ComponentType<{ size?: number; strokeWidth?: number }>>;

export type MobileAppPromoIcon = keyof typeof FEATURE_ICONS;

const ICON_NAMES = Object.keys(FEATURE_ICONS) as MobileAppPromoIcon[];

interface MobileFeature {
  icon?: string;
  title?: Localized;
}

export interface MobileAppPromoProps {
  kicker?: Localized;
  title?: Localized;
  highlight?: Localized;
  body?: Localized;
  features?: MobileFeature[];
  appStoreLabel?: Localized;
  appStoreSubLabel?: Localized;
  appStoreHref?: string;
  playStoreLabel?: Localized;
  playStoreSubLabel?: Localized;
  playStoreHref?: string;
  qrImage?: string;
  qrLabel?: Localized;
  qrCaption?: Localized;
  qrHref?: string;
  variant?: MobileAppPromoVariant;
  accent?: MobileAppPromoAccent;
  imagePosition?: MobileAppPromoImagePosition;
  screenImage?: string;
  secondaryScreenImage?: string;
  imageAlt?: Localized;
  paddingOuter?: PaddingBox;
  paddingInner?: PaddingBox;
  style?: CSSProperties;
}

type PromoVars = CSSProperties & {
  '--app-accent': string;
};

/** The stock 2×2 feature grid, used until the author supplies their own.
 *  Built per render so a language switch re-reads the catalogue. */
const defaultFeatures = (t: (key: string) => string): MobileFeature[] => [
  { icon: 'offline-watch', title: t('mobileAppPromo.feature.offline') },
  { icon: 'zap', title: t('mobileAppPromo.feature.fastAccess') },
  { icon: 'clock', title: t('mobileAppPromo.feature.resume') },
  { icon: 'target', title: t('mobileAppPromo.feature.tracking') },
];

export function MobileAppPromo({
  kicker,
  title,
  highlight,
  body,
  features,
  appStoreLabel,
  appStoreSubLabel,
  appStoreHref = '#',
  playStoreLabel,
  playStoreSubLabel,
  playStoreHref = '#',
  qrImage,
  qrLabel,
  qrCaption,
  qrHref,
  variant = 'immersive',
  accent = 'brand',
  imagePosition = 'right',
  screenImage,
  secondaryScreenImage,
  imageAlt,
  paddingOuter,
  paddingInner,
  style,
}: MobileAppPromoProps) {
  const { t } = useTranslation('site-builder-blocks');
  const kickerText = kicker ?? t('mobileAppPromo.kicker');
  const titleText = title ?? t('mobileAppPromo.title');
  const highlightText = highlight ?? t('mobileAppPromo.highlight');
  const bodyText = body ?? t('mobileAppPromo.body');
  const appStoreName = appStoreLabel ?? t('mobileAppPromo.store.appStore');
  const appStoreSub = appStoreSubLabel ?? t('mobileAppPromo.store.appStoreSub');
  const playStoreName = playStoreLabel ?? t('mobileAppPromo.store.playStore');
  const playStoreSub = playStoreSubLabel ?? t('mobileAppPromo.store.playStoreSub');
  const qrLabelText = qrLabel ?? t('mobileAppPromo.qrLabel');
  const imageAltText = imageAlt ?? t('mobileAppPromo.imageAlt');
  // Capped at 4: the grid is a fixed 2×2 and a 5th item opens a ragged
  // third row that throws the band's balance off.
  const featureList = asList<MobileFeature>(features ?? defaultFeatures(t)).slice(0, 4);
  const accentVar = ACCENT_TOKENS[accent] ?? ACCENT_TOKENS.brand;
  // Matches the min-height the hand-rolled buttons used to occupy, so the
  // copy column's rhythm is unchanged by the swap to official artwork.
  const badgeHeight = variant === 'compact' ? 42 : 46;

  const shellStyle: PromoVars = {
    ...(sectionInner as CSSProperties),
    ...resolvePaddingStyle(paddingInner),
    '--app-accent': accentVar,
  };

  return (
    <BlockRoot
      as="section"
      className={`map-section map-${variant} map-image-${imagePosition}`}
      style={{ ...resolvePaddingStyle(paddingOuter), ...style }}
      aria-labelledby="mobile-promo-title"
    >
      <div className="map-shell" style={shellStyle}>
        <div className="map-copy">
          {kickerText && <div className="map-kicker">{tx(kickerText)}</div>}

          <h2 id="mobile-promo-title" className="map-title">
            {tx(titleText)}
            {highlightText && (
              <>
                <br />
                <span>{tx(highlightText)}</span>
              </>
            )}
          </h2>

          {bodyText && <p className="map-body">{tx(bodyText)}</p>}

          {featureList.length > 0 && (
            <ul className="map-features">
              {featureList.map((feature, index) => (
                <FeatureRow key={`${tx(feature.title)}-${index}`} feature={feature} />
              ))}
            </ul>
          )}

          <div className="map-actions">
            <StoreBadge
              store="appstore"
              href={appStoreHref}
              height={badgeHeight}
              ariaLabel={storeAria(appStoreSub, appStoreName)}
            />
            <StoreBadge
              store="googleplay"
              href={playStoreHref}
              height={badgeHeight}
              ariaLabel={storeAria(playStoreSub, playStoreName)}
            />
          </div>
        </div>

        <div className="map-media">
          {qrImage && (
            <QrBlock
              image={qrImage}
              label={tx(qrLabelText)}
              caption={tx(qrCaption)}
              href={qrHref}
              imageAlt={t('mobileAppPromo.qrAlt')}
            />
          )}
          <DeviceCluster
            screenImage={screenImage}
            secondaryScreenImage={secondaryScreenImage}
            imageAlt={tx(imageAltText)}
          />
        </div>
      </div>

      <style dangerouslySetInnerHTML={{ __html: mobileAppPromoCss }} />
    </BlockRoot>
  );
}

/**
 * The official badge artwork carries its own (English) wording, so the
 * authored store lines no longer render — they survive as the link's
 * accessible name, which keeps the tenant's own language for screen
 * readers. Empty authored text falls back to the badge's default label.
 */
function storeAria(subLabel: Localized | undefined, label: Localized | undefined) {
  const text = `${tx(subLabel)} ${tx(label)}`.trim();
  return text || undefined;
}

function FeatureRow({ feature }: { feature: MobileFeature }) {
  const name = pickEnum<MobileAppPromoIcon>(feature.icon, ICON_NAMES, 'check');
  const Icon = FEATURE_ICONS[name];
  return (
    <li className="map-feature">
      <Icon size={15} strokeWidth={1.6} />
      <span>{tx(feature.title)}</span>
    </li>
  );
}

/**
 * Bare scan-to-download block: label, white QR tile, optional caption. No
 * card chrome — it sits directly on the band between the copy and the
 * phones, which is what keeps the strip from reading as three boxes.
 */
function QrBlock({
  image,
  label,
  caption,
  href,
  imageAlt,
}: {
  image: string;
  label: string;
  caption: string;
  href?: string;
  imageAlt: string;
}) {
  const inner = (
    <>
      {label && <div className="map-qr-label">{label}</div>}
      {/* The frame is what stops the white tile reading as a sticker taped
          onto the band: a hairline ring at a small remove, with the tile's
          own edge inside it. */}
      <div className="map-qr-frame">
        <div className="map-qr-code">
          <img src={image} alt={label || imageAlt} loading="lazy" decoding="async" />
        </div>
      </div>
      {caption && <div className="map-qr-caption">{caption}</div>}
    </>
  );

  if (href) {
    return (
      <a className="map-qr map-qr-link" href={href}>
        {inner}
      </a>
    );
  }
  return <div className="map-qr">{inner}</div>;
}

/**
 * Two upright images, the main one in front and left, a smaller second one
 * behind its right shoulder. Both are taller than the band and get cropped
 * by the shell's overflow.
 *
 * <p>Each slot is skipped when it has no image — with nothing drawn around
 * the screenshot there is no empty device left to render.
 */
function DeviceCluster({
  screenImage,
  secondaryScreenImage,
  imageAlt,
}: {
  screenImage?: string;
  secondaryScreenImage?: string;
  imageAlt: string;
}) {
  // falls back to the main screenshot so one authored image still fills
  // both slots
  const altImage = secondaryScreenImage ?? screenImage;
  if (!screenImage && !altImage) return null;

  return (
    <div className="map-devices" role="img" aria-label={imageAlt}>
      {altImage && (
        <img
          className="map-phone map-phone-alt"
          src={altImage}
          alt=""
          aria-hidden="true"
          loading="lazy"
          decoding="async"
        />
      )}
      {screenImage && (
        <img
          className="map-phone map-phone-main"
          src={screenImage}
          alt=""
          aria-hidden="true"
          loading="lazy"
          decoding="async"
        />
      )}
    </div>
  );
}

const mobileAppPromoCss = `
.map-section {
  padding: var(--space-10, 40px) var(--space-6, 24px);
  background: transparent;
}
/* overflow: hidden is load-bearing — it crops the phones at the band edge. */
.map-shell {
  position: relative;
  isolation: isolate;
  overflow: hidden;
  display: grid;
  /* The copy track hugs its content (it is capped at 480px anyway) so the
     leftover width lands in the media track, where the QR can centre inside
     it — with a 1fr copy track the slack piled up left of the QR and pinned
     the code against the phones. */
  grid-template-columns: minmax(0, auto) minmax(0, 1fr);
  align-items: center;
  gap: clamp(22px, 3vw, 48px);
  min-height: 340px;
  padding: clamp(26px, 2.9vw, 40px) clamp(24px, 3.2vw, 48px);
  color: #ffffff;
  border-radius: var(--radius-2xl, 28px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  background:
    radial-gradient(120% 140% at 8% 0%,
      color-mix(in srgb, var(--app-accent) 9%, transparent), transparent 46%),
    linear-gradient(180deg, #101114, #0a0b0d);
}
.map-compact .map-shell {
  min-height: 296px;
  padding: clamp(22px, 2.8vw, 34px) clamp(22px, 3vw, 40px);
  border-radius: var(--radius-xl, 24px);
}
.map-image-left .map-shell { grid-template-columns: minmax(0, 1fr) minmax(0, auto); }
.map-image-left .map-shell > .map-copy  { order: 2; }
.map-image-left .map-shell > .map-media { order: 1; flex-direction: row-reverse; }
.map-copy { position: relative; z-index: 2; max-width: 480px; }
.map-kicker {
  color: color-mix(in srgb, currentColor 52%, transparent);
  font-family: var(--font-family-body, inherit);
  font-size: 11px; font-weight: 600;
  letter-spacing: 0.17em; text-transform: uppercase;
}
.map-title {
  margin: 14px 0 0;
  color: currentColor;
  font-family: var(--font-family-display, Inter, system-ui, sans-serif);
  font-size: clamp(26px, 2.8vw, 37px);
  font-weight: 650; line-height: 1.16;
  letter-spacing: -0.025em;
}
.map-compact .map-title { font-size: clamp(24px, 2.3vw, 32px); margin-top: 12px; }
.map-title span { color: color-mix(in srgb, currentColor 92%, transparent); }
.map-body {
  margin: 12px 0 0; max-width: 420px;
  color: color-mix(in srgb, currentColor 50%, transparent);
  font-family: var(--font-family-body, inherit);
  font-size: 13px; line-height: 1.6;
}
/* Fixed 2×2: the band's rhythm depends on two short columns, not on a
   reflowing auto-fit track that turns into 1×4 at the first squeeze. */
.map-features {
  list-style: none; margin: 22px 0 0; padding: 0;
  display: grid; grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 11px clamp(18px, 2vw, 34px);
  max-width: 420px;
}
.map-compact .map-features { margin-top: 18px; gap: 9px clamp(16px, 1.8vw, 28px); }
.map-feature {
  display: flex; align-items: center; gap: 9px;
  color: color-mix(in srgb, currentColor 78%, transparent);
  font-size: 12.5px; font-weight: 500; line-height: 1.3;
}
.map-feature svg {
  flex: 0 0 auto;
  color: color-mix(in srgb, currentColor 60%, transparent);
}
.map-actions {
  display: flex; flex-wrap: wrap; align-items: center;
  gap: 10px; margin-top: clamp(18px, 2vw, 28px);
}
/* Same StoreBadge component the footer renders. The band's surface is a
   fixed near-black in BOTH themes, so the badge vars are pinned to the
   black-pill treatment here instead of flipping to the white variant on
   :root[data-theme="dark"] — the ring is brightened so the pill still
   separates from the band. */
.map-actions .lms-store-badge,
:root[data-theme='dark'] .map-actions .lms-store-badge {
  --store-badge-bg: #000000;
  --store-badge-fg: #ffffff;
  --store-badge-border: rgba(255, 255, 255, 0.4);
}
/* Phones stay pinned to the band's far edge; the QR takes the slack. */
.map-media {
  display: flex; align-items: center; align-self: stretch;
  justify-content: flex-end;
  gap: clamp(18px, 2.4vw, 44px);
}
/* ---- Scan to download -------------------------------------------------- */
/* flex: 1 — the block stretches across the gap between the copy and the
   phones, and align-items centres the label/tile/caption inside it, so the
   code lands in the middle of that space instead of at its right edge. */
.map-qr {
  flex: 1 1 auto;
  display: flex; flex-direction: column; align-items: center; gap: 10px;
  text-decoration: none; color: inherit;
  transition: transform 200ms ease;
}
.map-qr-label {
  color: color-mix(in srgb, currentColor 88%, transparent);
  font-family: var(--font-family-display, inherit);
  font-size: 13px; font-weight: 600; letter-spacing: -0.005em;
  text-align: center;
}
/* Two hairlines, not one: the outer ring sits a corner-radius away from the
   tile so the gap reads as deliberate matting, and the tile carries its own
   inset line so the white edge is drawn rather than just ending. */
.map-qr-frame {
  padding: 11px; border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.03);
  transition: border-color 200ms ease, background-color 200ms ease;
}
.map-compact .map-qr-frame { padding: 9px; border-radius: 11px; }
.map-qr-code {
  width: clamp(98px, 9vw, 126px); aspect-ratio: 1;
  padding: 7px; border-radius: 6px;
  background: #ffffff;
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.08);
}
.map-compact .map-qr-code { width: clamp(88px, 7.8vw, 110px); }
.map-qr-code img { width: 100%; height: 100%; display: block; object-fit: contain; }
.map-qr-caption {
  max-width: 150px;
  color: color-mix(in srgb, currentColor 46%, transparent);
  font-size: 11px; font-weight: 500; line-height: 1.4;
  text-align: center;
}
/* ---- Devices ----------------------------------------------------------- */
/* The two images lean opposite ways — that mirrored splay is the shape of
   the cluster, so --phone-lean is set per image, not shared. --phone-inset
   is the price of the lean: rotating a tall box throws a corner past its
   box and the shell's overflow: hidden shaves whatever lands outside, so
   the pair is inset instead of sitting flush to the content edge. */
.map-devices {
  position: relative;
  flex: 0 0 auto; align-self: stretch;
  --phone-inset: clamp(8px, 0.9vw, 12px);
  width: clamp(276px, 33.3vw, 440px);
  min-height: 270px;
}
.map-compact .map-devices { width: clamp(236px, 28.4vw, 375px); min-height: 224px; }
/* No bezel, no screen frame — the authored image IS the device. But the box
   still carries the old device geometry (upright 9:19.2, taller than the
   band) because that is what makes the pair read as big and cropped;
   letting the image fall back to its natural height parks it small and
   centred with nothing running off the edge.
   object-fit: contain — the authored render already includes its own bezel,
   so cover would shave it; contain scales it up to the box without ever
   clipping the device. object-position pins it to the top so any slack
   lands at the bottom, where the band crops anyway.
   Placement lives in one transform (offset then lean) so the hover lift can
   add itself through --phone-lift without restating the rotation. */
.map-phone {
  position: absolute;
  display: block;
  aspect-ratio: 9 / 19.2;
  object-fit: contain;
  object-position: center top;
  transform-origin: var(--phone-origin, 50% 50%);
  /* perspective() must precede rotateY() for the turn to read as depth
     rather than a flat squash. Images with no --phone-turn stay face-on. */
  transform:
    translate(var(--phone-x, 0px), calc(var(--phone-y, 0px) + var(--phone-lift, 0px)))
    rotate(var(--phone-lean, 0deg))
    perspective(var(--phone-depth, 1200px))
    rotateY(var(--phone-turn, 0deg));
  transition: transform 380ms cubic-bezier(0.2, 0.8, 0.2, 1);
}
/* The pair stands upright with a shared lean; the main image leads on the
   left, the smaller one sits behind its right shoulder and a notch lower,
   exactly as both run off the bottom of the band. Vertical staging is
   --phone-y, not a top offset, so it composes with the hover lift.
   Negative --phone-y lifts the pair above the shell's padding box so they
   start near the band's top edge, the way the reference does. */
/* Front image: face-on to the reader, leaning slightly left. No turn — it
   is the one screenshot that has to stay legible.
   --phone-x slides it toward its neighbour so the two bodies overlap at
   the bottom corner: the opposing leans already close the gap downward,
   and this pushes them into each other rather than letting the pair read
   as two separate objects. It shifts the front image (not the rear one)
   because the rear image's distance to the band edge is fixed. */
/* min-height is the floor that keeps both bodies running off the bottom on
   an unusually tall band: normally the width-derived 9:19.2 height is the
   larger of the two, but a long copy column would otherwise grow the band
   past the images and they would stop reading as cropped. */
.map-phone-main {
  left: 0; top: 0; z-index: 2;
  width: clamp(150px, 15.9vw, 210px);
  min-height: calc(100% + 60px);
  --phone-lean: -2deg;
  --phone-x: clamp(30px, 4.2vw, 56px);
  --phone-y: -14px;
}
/* Rear image: leans the other way and is turned away from the reader,
   pivoting on its bottom-left corner so the near edge stays planted next
   to the front one and the far edge recedes. */
.map-phone-alt {
  right: var(--phone-inset); top: 0; z-index: 1;
  width: clamp(126px, 13.6vw, 280px);
  min-height: calc(100% + 50px);
  --phone-lean: 3deg;
  --phone-turn: -11deg;
  --phone-origin: 0% 100%;
  --phone-y: 8px;
  opacity: 0.94;
}
.map-compact .map-phone-main { width: clamp(128px, 13.5vw, 178px); --phone-y: -12px; }
.map-compact .map-phone-alt  { width: clamp(108px, 11.6vw, 152px); --phone-y: 6px; }
@media (hover: hover) and (prefers-reduced-motion: no-preference) {
  /* The lift itself comes from .lms-store-badge in portal.css; only the
     ring brightens here, since the pinned border var overrides it. */
  .map-actions .lms-store-badge:hover { --store-badge-border: rgba(255, 255, 255, 0.62); }
  .map-qr-link:hover { transform: translateY(-2px); }
  .map-qr-link:hover .map-qr-frame {
    border-color: rgba(255, 255, 255, 0.28);
    background: rgba(255, 255, 255, 0.06);
  }
  .map-shell:hover .map-phone-main { --phone-lift: -6px; }
  .map-shell:hover .map-phone-alt  { --phone-lift: -10px; }
}
.map-actions .lms-store-badge:focus-visible,
.map-qr-link:focus-visible {
  outline: 2px solid var(--app-accent);
  outline-offset: 3px;
}
/* Stack once the copy column can no longer hold the 2×2 grid beside the
   media group; QR and phones stay a pair so the strip keeps its shape. */
@media (max-width: 1040px) {
  .map-shell, .map-image-left .map-shell {
    grid-template-columns: 1fr;
    gap: clamp(20px, 3vw, 34px);
  }
  .map-image-left .map-shell > .map-copy  { order: 0; }
  .map-image-left .map-shell > .map-media { order: 1; flex-direction: row; }
  .map-copy { max-width: none; }
  .map-media { justify-content: center; align-self: auto; }
  /* Stacked: QR and phones travel as a centred pair, so the code must stop
     absorbing the row's slack or it would drift away from the devices. */
  .map-qr { flex: 0 0 auto; }
  .map-devices, .map-compact .map-devices { min-height: 250px; }
}
@media (max-width: 760px) {
  .map-shell, .map-compact .map-shell {
    padding: clamp(22px, 5vw, 34px);
    border-radius: var(--radius-xl, 24px);
    min-height: 0;
  }
  .map-title, .map-compact .map-title { font-size: clamp(26px, 7vw, 34px); }
  .map-features, .map-compact .map-features { max-width: none; gap: 10px 18px; }
  .map-media { gap: 18px; }
  .map-devices, .map-compact .map-devices {
    width: clamp(200px, 48vw, 266px); min-height: 220px;
  }
  .map-devices, .map-compact .map-devices { --phone-inset: 10px; }
  .map-phone-main, .map-compact .map-phone-main {
    width: clamp(112px, 25vw, 136px); --phone-y: -8px;
  }
  .map-phone-alt, .map-compact .map-phone-alt {
    width: clamp(84px, 18.5vw, 100px); --phone-y: 4px;
  }
}
@media (max-width: 520px) {
  .map-section { padding-inline: 14px; }
  .map-features, .map-compact .map-features { grid-template-columns: 1fr; }
  /* !important: StoreBadge sets its height inline, and both badges must
     shrink together to stay on one row at this width. */
  .map-actions .lms-store-badge { height: 40px !important; }
  .map-media { gap: 14px; }
  .map-qr-code, .map-compact .map-qr-code { width: 96px; }
  /* One phone only — two at this width leaves neither readable. */
  .map-phone-alt { display: none; }
  .map-devices, .map-compact .map-devices { width: 140px; min-height: 200px; }
  .map-phone-main, .map-compact .map-phone-main { width: 140px; left: 0; }
}
@media (prefers-reduced-motion: reduce) {
  .map-actions .lms-store-badge, .map-qr, .map-phone { transition: none !important; }
}
`;
